import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFirebase, hasPasswordProvider, reauthenticateForAccountDeletion } from '@/firebase';

const ACCOUNT_DELETION_CONFIRMATION = 'delete-account';

export function DeleteAccountSection() {
  const { t, i18n } = useTranslation();
  const { user, backend } = useFirebase();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const confirmationPhrase = t('accountDeletion.confirmationPhrase');
  const needsPassword = Boolean(user && hasPasswordProvider(user));

  const submit = async () => {
    if (!user || confirmation !== confirmationPhrase) return;
    setBusy(true);
    setError('');
    try {
      await reauthenticateForAccountDeletion(user, password);
      await user.getIdToken(true);
      await backend.billing.requestAccountDeletion(
        ACCOUNT_DELETION_CONFIRMATION,
        i18n.resolvedLanguage ?? i18n.language
      );
      await user.getIdToken(true);
      window.location.assign('/account-pending-deletion');
    } catch (cause) {
      console.error('Account deletion request failed', cause);
      setError(t('accountDeletion.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ mt: 4, p: 4, border: '1px solid', borderColor: 'error.main' }}>
      <Typography variant="h5" color="error" gutterBottom>{t('accountDeletion.title')}</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>{t('accountDeletion.summary')}</Typography>
      <Button color="error" variant="outlined" startIcon={<DeleteForeverIcon />} onClick={() => setOpen(true)}>
        {t('accountDeletion.open')}
      </Button>
      <Dialog open={open} onClose={busy ? undefined : () => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('accountDeletion.dialogTitle')}</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>{t('accountDeletion.warning')}</Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>{t('accountDeletion.delay')}</Typography>
          {needsPassword && (
            <TextField
              fullWidth type="password" label={t('accountDeletion.password')}
              value={password} onChange={(event) => setPassword(event.target.value)} sx={{ mb: 2 }}
            />
          )}
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('accountDeletion.typePhrase')} <strong>{confirmationPhrase}</strong>
          </Typography>
          <TextField fullWidth value={confirmation} onChange={(event) => setConfirmation(event.target.value)} />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>{t('common.cancel')}</Button>
          <Button
            color="error" variant="contained" onClick={submit}
            disabled={busy || confirmation !== confirmationPhrase || (needsPassword && !password)}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('accountDeletion.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
